import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import styles from './staffPreparationTaskDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import {
    GET_STAFF_PREPARATION_TASKS_BY_DAYCARE,
    UPDATE_STAFF_PREPARATION_TASK
} from '@/modules/API/staff/staffPreparationTask'
import StaffPreparationTaskForm from './staffPreparationTaskForm'
import { StaffPreparationTaskDialogEditProps } from './staffPreparationTaskDialog'
import { useFutureWorkCountForStaffPreparationTask } from '@/hooks/useFutureWorkCountForStaffPreparationTask'

const EditStaffPreparationTaskDialog: FunctionComponent<StaffPreparationTaskDialogEditProps> = ({
    open,
    handleClose,
    daycare,
    staffPreparationTaskId,
    staffPreparationTaskName,
    setStaffPreparationTaskName,
    staffPreparationTaskDuration,
    setStaffPreparationTaskDuration,
    updateOneStaffPreparationTask
}) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newName, setNewName] = useState<string>(staffPreparationTaskName)
    const [newDuration, setNewDuration] = useState<string>(staffPreparationTaskDuration)
    const [verifyDuration, setVerifyDuration] = useState<boolean>(true)

    const workCount: number = useFutureWorkCountForStaffPreparationTask(daycare.id, staffPreparationTaskId, !open)

    const [updateStaffPreparationTask] = useMutation(UPDATE_STAFF_PREPARATION_TASK, {
        variables: {
            staffPreparationTaskId,
            daycareId: daycare.id,
            staffPreparationTask: {
                name: newName,
                duration: parseInt(newDuration)
            }
        },
        refetchQueries: [{ query: GET_STAFF_PREPARATION_TASKS_BY_DAYCARE, variables: { daycareId: daycare.id } }],
        onCompleted: () => {
            updateOneStaffPreparationTask({
                id: staffPreparationTaskId,
                daycareId: daycare.id,
                name: newName,
                duration: parseInt(newDuration)
            })

            setStaffPreparationTaskName(newName)
            setStaffPreparationTaskDuration(newDuration)

            handleClose()

            showNotificationSuccess(t('staffPreparationTaskUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffPreparationTask'))
        }
    })

    const disabled: boolean =
        !verifyDuration || (staffPreparationTaskName === newName && staffPreparationTaskDuration === newDuration)

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('updateStaffPreparationTask')} />
            <DialogBody className={styles.DialogBody}>
                <StaffPreparationTaskForm
                    name={newName}
                    setName={setNewName}
                    duration={newDuration}
                    setDuration={setNewDuration}
                    setDurationValid={setVerifyDuration}
                />
                {workCount > 0 && <p className={styles.Text}>{t('taskInFutureWorksEdit', { count: workCount })}</p>}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateStaffPreparationTask}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditStaffPreparationTaskDialog
